import { useCallback, useEffect, useState } from "react";
import {
  Banner, Card, CardHead, Empty, Freshness, Modal, Num, Pill, Skeleton, Stat, Toasts, useToasts,
} from "../components/ui";
import { IconRecon } from "../components/icons";

interface ReconData {
  run: { id: string; run_at: string; compared: number; matched: number; break_count: number } | null;
  breaks: {
    id: string;
    source: string;
    asset: string;
    external_amount: string;
    ledger_amount: string;
    delta: string;
    status: string;
    note: string | null;
  }[];
}

const auth = () => ({
  authorization: `Bearer ${sessionStorage.getItem("otc.session") ?? ""}`,
  "content-type": "application/json",
});
const POLL_MS = 60_000;

type Pending = { id: string; status: "acknowledged" | "resolved" };

export function Recon({ canAct }: { canAct: boolean }) {
  const [data, setData] = useState<ReconData | null>(null);
  const [error, setError] = useState("");
  const [at, setAt] = useState<number | null>(null);
  const [confirm, setConfirm] = useState<Pending | null>(null);
  const toast = useToasts();

  const load = useCallback(async () => {
    const res = await fetch("/api/recon/latest", { headers: auth() });
    if (res.ok) {
      setData(await res.json());
      setAt(Date.now());
      setError("");
    } else setError("Could not load reconciliation data.");
  }, []);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  async function act({ id, status }: Pending, note: string) {
    const res = await fetch(`/api/recon/breaks/${id}`, {
      method: "PATCH",
      headers: auth(),
      body: JSON.stringify({ status, ...(note ? { note } : {}) }),
    });
    toast.push(res.ok ? "ok" : "error", res.ok ? `Break ${status}.` : "Could not update the break.");
    setConfirm(null);
    await load();
  }

  if (!data && !error) return <Skeleton rows={5} />;
  if (error) return <Banner tone="error">{error}</Banner>;
  if (!data?.run) {
    return (
      <Card>
        <Empty icon={<IconRecon size={20} />} title="No reconciliation run yet"
               body="The first run lands within 30 minutes of deploy, then every 30 minutes after." />
      </Card>
    );
  }

  const { run } = data;
  const clean = run.break_count === 0;

  return (
    <>
      <div className="section-head">
        <p className="page-intro" style={{ marginBottom: 0 }}>
          External venue and bank positions compared against the desk's own ledger, every 30 minutes.
        </p>
        <Freshness at={at} intervalMs={POLL_MS} />
      </div>

      <div className="grid grid-stats grid-stats-3">
        <Stat label="Positions compared" value={<Num value={run.compared} />}
              sub={`run ${new Date(run.run_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}`} />
        <Stat label="Matched" value={<Num value={run.matched} />} tone="gain"
              sub={run.compared ? `${((run.matched / run.compared) * 100).toFixed(1)}% of positions` : undefined} />
        <Stat label="Breaks" value={<Num value={run.break_count} />} tone={clean ? "gain" : "loss"}
              sub={clean ? "books agree with every source" : "needs investigation"} />
      </div>

      <div className="section">
        {clean ? (
          <Card>
            <Empty icon={<IconRecon size={20} />} title="Everything reconciles"
                   body="Every compared position agrees with the external source. Nothing to action." />
          </Card>
        ) : (
          <Card pad={false}>
            <CardHead title={`Open breaks (${data.breaks.length})`} />
            <div className="table-wrap flush">
              <table>
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Asset</th>
                    <th className="right">External</th>
                    <th className="right">Ledger</th>
                    <th className="right">Delta</th>
                    <th>Status</th>
                    {canAct && <th />}
                  </tr>
                </thead>
                <tbody>
                  {data.breaks.map((b) => (
                    <tr key={b.id}>
                      <td style={{ textTransform: "capitalize", fontWeight: 500 }}>{b.source}</td>
                      <td>{b.asset}</td>
                      <td className="right num"><Num value={b.external_amount} dp={8} /></td>
                      <td className="right num"><Num value={b.ledger_amount} dp={8} /></td>
                      <td className="right num loss"><Num value={b.delta} dp={8} /></td>
                      <td>
                        <Pill tone={b.status === "open" ? "bad" : b.status === "resolved" ? "good" : "warn"} dot>
                          {b.status}
                        </Pill>
                        {b.note && <div className="dim t-sm" style={{ marginTop: 3 }}>{b.note}</div>}
                      </td>
                      {canAct && (
                        <td className="right">
                          {b.status === "open" && (
                            <button type="button" className="btn btn-sm btn-secondary"
                                    onClick={() => setConfirm({ id: b.id, status: "acknowledged" })}>
                              Acknowledge
                            </button>
                          )}
                          {b.status === "acknowledged" && (
                            <button type="button" className="btn btn-sm btn-secondary"
                                    onClick={() => setConfirm({ id: b.id, status: "resolved" })}>
                              Resolve
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {confirm && (
        <Modal
          title={confirm.status === "acknowledged" ? "Acknowledge this break?" : "Resolve this break?"}
          confirmLabel={confirm.status === "acknowledged" ? "Acknowledge" : "Resolve"}
          notePlaceholder="Note (optional): what you found, or what you did"
          body={
            confirm.status === "acknowledged"
              ? "Marks the break as seen and under investigation. It stays open until resolved."
              : "Marks the break as investigated and closed. Recorded against your account."
          }
          onCancel={() => setConfirm(null)}
          onConfirm={(note) => void act(confirm, note)}
        />
      )}

      <Toasts items={toast.items} onDone={toast.dismiss} />
    </>
  );
}
